import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { mockEmployees } from '../mockData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Download,
  Printer,
  Calendar,
  DollarSign,
  User,
  CheckCircle2,
  RefreshCw,
  Layers,
  Sparkles,
  Info
} from 'lucide-react';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const AVAILABLE_YEARS = [2024, 2025, 2026, 2027];

// Converts Indian Rupees amount to words
const numberToWords = (num) => {
  const integerPart = Math.floor(Math.abs(Number(num) || 0));
  if (integerPart === 0) return 'Zero Rupees Only';

  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const inWords = (n) => {
    if (n < 20) return a[n];
    const digit = n % 10;
    return b[Math.floor(n / 10)] + (digit ? ' ' + a[digit] : '');
  };

  let str = '';
  let remaining = integerPart;

  const crore = Math.floor(remaining / 10000000);
  remaining %= 10000000;
  const lakh = Math.floor(remaining / 100000);
  remaining %= 100000;
  const thousand = Math.floor(remaining / 1000);
  remaining %= 1000;
  const hundred = Math.floor(remaining / 100);
  const rem = remaining % 100;

  if (crore) str += `${inWords(crore)} Crore `;
  if (lakh) str += `${inWords(lakh)} Lakh `;
  if (thousand) str += `${inWords(thousand)} Thousand `;
  if (hundred) str += `${inWords(hundred)} Hundred `;
  if (rem) str += `${str ? 'and ' : ''}${inWords(rem)} `;

  return `${str.trim()} Rupees Only`;
};

const Finance = () => {
  const { user, isEmployee } = useAuth();

  // Selected Pay Period (default month 9 = Sept, year 2026 as per prompt)
  const [selectedMonth, setSelectedMonth] = useState(9);
  const [selectedYear, setSelectedYear] = useState(2026);

  // Target User & Org ID
  const defaultUserId = isEmployee ? (user?.id || user?.userId || 101) : 101;
  const defaultOrgId = isEmployee ? (user?.orgId || user?.org_id || 10) : 10;

  const [targetUserId, setTargetUserId] = useState(defaultUserId);
  const [targetOrgId, setTargetOrgId] = useState(defaultOrgId);

  // List of employees for admin dropdown
  const initialEmployees = useMemo(() => [
    {
      id: 101,
      userId: 101,
      name: 'Rajesh Kumar',
      firstName: 'Rajesh',
      lastName: 'Kumar',
      designation: 'Senior Software Engineer',
      department: 'Engineering',
      pan: 'ABCDE1234F',
      bankAccount: '•••• •••• 1234',
      ifsc: 'HDFC0001234',
      company: 'TechApurva Global',
      orgId: 10,
    },
    ...mockEmployees.map((emp, i) => ({
      ...emp,
      id: emp.id || 102 + i,
      userId: emp.id || 102 + i,
    })),
  ], []);

  const [employeeOptions, setEmployeeOptions] = useState(initialEmployees);
  
  // API State
  const [salaryData, setSalaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLiveApi, setIsLiveApi] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // If user profile is loaded and is an employee, bind to user's identity
  useEffect(() => {
    if (isEmployee && user) {
      if (user.id || user.userId) setTargetUserId(user.id || user.userId);
      if (user.orgId || user.org_id) setTargetOrgId(user.orgId || user.org_id);
    }
  }, [isEmployee, user]);

  // Try to load system users if admin
  useEffect(() => {
    if (!isEmployee) {
      userService.getAll()
        .then((data) => {
          if (Array.isArray(data) && data.length > 0) {
            // Keep 101 at the top if not present
            const has101 = data.some((u) => String(u.id) === '101' || String(u.userId) === '101');
            setEmployeeOptions(has101 ? data : [initialEmployees[0], ...data]);
          }
        })
        .catch(() => {
          // Keep default employees as fallback
        });
    }
  }, [isEmployee, initialEmployees]);

  // Selected employee metadata
  const selectedEmployeeInfo = useMemo(() => {
    const found = employeeOptions.find(
      (e) => String(e.id) === String(targetUserId) || String(e.userId) === String(targetUserId)
    );
    if (found) {
      return {
        id: found.id || found.userId || targetUserId,
        name: [found.firstName, found.middleName, found.lastName].filter(Boolean).join(' ') || found.name || 'Rajesh Kumar',
        designation: found.designation || 'Senior Software Engineer',
        department: found.department || 'Engineering',
        pan: found.pan || 'ABCDE1234F',
        bankAccount: found.bankAccount || '•••• •••• 1234',
        ifsc: found.ifsc || 'HDFC0001234',
        email: found.email || 'employee@techapurva.com',
        company: found.company || 'TechApurva Global'
      };
    }
    return {
      id: targetUserId,
      name: isEmployee ? (user?.name || 'Employee User') : 'Rajesh Kumar',
      designation: isEmployee ? (user?.designation || 'Specialist') : 'Senior Software Engineer',
      department: isEmployee ? (user?.department || 'Operations') : 'Engineering',
      pan: 'ABCDE1234F',
      bankAccount: '•••• •••• 1234',
      ifsc: 'HDFC0001234',
      email: isEmployee ? (user?.email || 'employee@techapurva.com') : 'rajesh.kumar@techapurva.com',
      company: user?.company || 'TechApurva Global'
    };
  }, [employeeOptions, targetUserId, user, isEmployee]);

  // Fetch salary details from API
  const fetchSalaryDetails = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await userService.getSalary(targetUserId, {
        orgId: targetOrgId,
        month: selectedMonth,
        year: selectedYear,
      });

      if (resp && typeof resp === 'object') {
        setSalaryData(resp);
        setIsLiveApi(true);
      } else {
        throw new Error('Invalid salary response');
      }
    } catch (err) {
      console.warn('Salary API request failed, applying standard template with returned components:', err);
      setIsLiveApi(false);

      // Fallback object matching the structure provided in the prompt
      setSalaryData({
        userId: targetUserId,
        orgId: targetOrgId,
        month: selectedMonth,
        year: selectedYear,
        totalCompensation: null,
        variable: null,
        components: [
          {
            salaryFieldId: 1,
            name: 'Basic Salary',
            fieldType: 'EARNING',
            value: null,
          },
          {
            salaryFieldId: 2,
            name: 'House Rent Allowance (HRA)',
            fieldType: 'EARNING',
            value: null,
          },
          {
            salaryFieldId: 4,
            name: 'Remote Work Stipend',
            fieldType: 'EARNING',
            value: null,
          },
          {
            salaryFieldId: 7,
            name: 'Provident Fund (PF)',
            fieldType: 'DEDUCTION',
            value: null,
          },
          {
            salaryFieldId: 9,
            name: 'Professional Tax',
            fieldType: 'DEDUCTION',
            value: null,
          },
        ],
      });
    } finally {
      setLoading(false);
    }
  }, [targetUserId, targetOrgId, selectedMonth, selectedYear]);

  useEffect(() => {
    fetchSalaryDetails();
  }, [fetchSalaryDetails]);

  // Parse Earnings & Deductions
  const { earnings, deductions, grossAmount, deductionsAmount, netAmount } = useMemo(() => {
    const rawComponents = salaryData?.components || [];

    // Separate into earnings and deductions
    const earnList = [];
    const dedList = [];

    // Baseline fallback values if value in API is null so preview looks realistic
    const fallbackValues = {
      'Basic Salary': 60000,
      'House Rent Allowance (HRA)': 30000,
      'Remote Work Stipend': 10000,
      'Special Allowance': 20000,
      'Provident Fund (PF)': 12000,
      'Professional Tax': 2500,
      'Health Insurance': 2000,
    };

    rawComponents.forEach((comp) => {
      const type = (comp.fieldType || 'EARNING').toUpperCase();
      const numericVal = comp.value !== null && comp.value !== undefined ? Number(comp.value) : (fallbackValues[comp.name] || 0);

      const enriched = {
        ...comp,
        isNullable: comp.value === null,
        displayValue: numericVal,
      };

      if (type === 'DEDUCTION') {
        dedList.push(enriched);
      } else {
        earnList.push(enriched);
      }
    });

    const gross = earnList.reduce((acc, curr) => acc + (curr.displayValue || 0), 0);
    const deds = dedList.reduce((acc, curr) => acc + (curr.displayValue || 0), 0);
    const net = Math.max(0, gross - deds);

    return {
      earnings: earnList,
      deductions: dedList,
      grossAmount: salaryData?.totalCompensation ? Number(salaryData.totalCompensation) : gross,
      deductionsAmount: deds,
      netAmount: net,
    };
  }, [salaryData]);

  // Handle PDF Download (captures the styled salary slip DOM element matching the Print option)
  const handleDownloadPdf = async () => {
    const element = document.getElementById('printable-salary-slip');
    if (!element) return;

    setDownloading(true);
    try {
      // Allow DOM rendering and font metrics to settle
      await new Promise((resolve) => setTimeout(resolve, 80));

      const canvas = await html2canvas(element, {
        scale: 2, // 2x resolution for crisp high-definition text
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById('printable-salary-slip');
          if (clonedElement) {
            clonedElement.style.boxShadow = 'none';
            clonedElement.style.borderRadius = '0';
          }
        },
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const margin = 10;
      const maxContentWidth = pdfWidth - margin * 2;
      const maxContentHeight = pdfHeight - margin * 2;

      let renderWidth = maxContentWidth;
      let renderHeight = (canvas.height * renderWidth) / canvas.width;

      // If document is slightly taller than 1 A4 page, scale down to fit cleanly on one single sheet
      if (renderHeight > maxContentHeight && renderHeight <= maxContentHeight * 1.25) {
        const scaleFactor = maxContentHeight / renderHeight;
        renderHeight = maxContentHeight;
        renderWidth = renderWidth * scaleFactor;
      }

      const posX = (pdfWidth - renderWidth) / 2;

      if (renderHeight <= maxContentHeight) {
        pdf.addImage(imgData, 'PNG', posX, margin, renderWidth, renderHeight);
      } else {
        // Multi-page handling if document exceeds single page
        let heightLeft = renderHeight;
        let position = margin;

        pdf.addImage(imgData, 'PNG', posX, position, renderWidth, renderHeight);
        heightLeft -= maxContentHeight;

        while (heightLeft > 0) {
          position = position - maxContentHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', posX, position, renderWidth, renderHeight);
          heightLeft -= maxContentHeight;
        }
      }

      pdf.save(
        `Salary-Slip-${MONTH_NAMES[selectedMonth - 1]}-${selectedYear}-EMP${selectedEmployeeInfo.id}.pdf`
      );
    } catch (err) {
      console.error('Failed to generate salary slip PDF:', err);
    } finally {
      setDownloading(false);
    }
  };

  // Handle Browser Native Print
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Print Specific CSS */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 10mm;
          }
          body * {
            visibility: hidden;
          }
          #printable-salary-slip, #printable-salary-slip * {
            visibility: visible;
          }
          #printable-salary-slip {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 12px;
            background: white !important;
            box-shadow: none !important;
            border: none !important;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-200 pb-5 no-print">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Finance</h1>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-medium">
              Salary & Payslips
            </Badge>
            {isLiveApi ? (
              <Badge className="bg-emerald-600 text-white flex items-center space-x-1">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                <span>Live API</span>
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-300">
                Preview Mode
              </Badge>
            )}
          </div>
          <p className="text-gray-500 mt-1">
            View monthly salary statements, component breakdowns, and download official payslips.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={fetchSalaryDetails}
            disabled={loading}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </Button>

          <Button
            variant="outline"
            onClick={handlePrint}
            className="flex items-center space-x-2 border-gray-300 text-gray-700 hover:bg-gray-100"
          >
            <Printer className="w-4 h-4" />
            <span>Print Payslip</span>
          </Button>

          <Button
            onClick={handleDownloadPdf}
            disabled={downloading || loading}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>{downloading ? 'Generating PDF...' : 'Download Salary Slip'}</span>
          </Button>
        </div>
      </div>

      {/* Role Notice & Filter Control Bar */}
      <Card className="shadow-sm border-gray-200 no-print">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
            {/* Employee Selector (Only editable for Admins) */}
            <div>
              <Label className="text-xs font-semibold uppercase text-gray-500 tracking-wider">
                Employee
              </Label>
              {isEmployee ? (
                <div className="mt-1.5 flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-800">
                    {selectedEmployeeInfo.name} (ID: {targetUserId})
                  </span>
                </div>
              ) : (
                <Select
                  value={String(targetUserId)}
                  onValueChange={(val) => setTargetUserId(val)}
                >
                  <SelectTrigger className="mt-1.5 w-full bg-white">
                    <SelectValue placeholder="Select Employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employeeOptions.map((emp) => {
                      const empId = emp.id || emp.userId;
                      const empName = [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(' ') || emp.name;
                      return (
                        <SelectItem key={empId} value={String(empId)}>
                          {empName} (ID: {empId})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Month Selector */}
            <div>
              <Label className="text-xs font-semibold uppercase text-gray-500 tracking-wider">
                Month
              </Label>
              <Select
                value={String(selectedMonth)}
                onValueChange={(val) => setSelectedMonth(Number(val))}
              >
                <SelectTrigger className="mt-1.5 w-full bg-white">
                  <SelectValue placeholder="Select Month" />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((name, index) => (
                    <SelectItem key={index + 1} value={String(index + 1)}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Year Selector */}
            <div>
              <Label className="text-xs font-semibold uppercase text-gray-500 tracking-wider">
                Year
              </Label>
              <Select
                value={String(selectedYear)}
                onValueChange={(val) => setSelectedYear(Number(val))}
              >
                <SelectTrigger className="mt-1.5 w-full bg-white">
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_YEARS.map((yr) => (
                    <SelectItem key={yr} value={String(yr)}>
                      {yr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Organization Info */}
            <div>
              <Label className="text-xs font-semibold uppercase text-gray-500 tracking-wider">
                Organization ID
              </Label>
              <div className="mt-1.5 flex items-center space-x-2">
                <Input
                  type="number"
                  value={targetOrgId}
                  disabled={isEmployee}
                  onChange={(e) => setTargetOrgId(e.target.value)}
                  className="bg-gray-50 border-gray-200"
                  placeholder="Org ID"
                />
              </div>
            </div>
          </div>

          {/* Quick Month Selector Bar */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500 mr-2 flex items-center">
              <Calendar className="w-3.5 h-3.5 mr-1 text-gray-400" />
              Quick Month:
            </span>
            {MONTH_NAMES.map((monthName, idx) => {
              const monthNum = idx + 1;
              const isSelected = selectedMonth === monthNum;
              return (
                <button
                  key={monthNum}
                  type="button"
                  onClick={() => setSelectedMonth(monthNum)}
                  className={`px-3 py-1 text-xs rounded-full transition-all ${
                    isSelected
                      ? 'bg-blue-600 text-white font-semibold shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {monthName.slice(0, 3)}
                </button>
              );
            })}
          </div>

          {!isEmployee && (
            <div className="mt-3 flex items-center text-xs text-blue-700 bg-blue-50/70 p-2.5 rounded-lg border border-blue-100">
              <Info className="w-4 h-4 mr-2 flex-shrink-0 text-blue-600" />
              <span>
                <strong>Note:</strong> API endpoint{' '}
                <code className="bg-blue-100/70 px-1 py-0.5 rounded font-mono text-[11px]">
                  api/v1/users/{targetUserId}/salary?orgId={targetOrgId}&month={selectedMonth}&year={selectedYear}
                </code>{' '}
                is designated for users with role <code>EMPLOYEE: 'employee'</code>.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI Cards Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 no-print">
        {/* Gross Earnings */}
        <Card className="border-l-4 border-l-blue-500 shadow-sm bg-white hover:shadow transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Earnings</p>
                <h3 className="text-2xl font-bold text-gray-900 mt-1">
                  ₹{grossAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </h3>
              </div>
              <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {earnings.length} Active Earning Components
            </p>
          </CardContent>
        </Card>

        {/* Total Deductions */}
        <Card className="border-l-4 border-l-rose-500 shadow-sm bg-white hover:shadow transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Deductions</p>
                <h3 className="text-2xl font-bold text-rose-600 mt-1">
                  -₹{deductionsAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </h3>
              </div>
              <div className="p-3 bg-rose-50 text-rose-600 rounded-xl">
                <Layers className="w-6 h-6" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {deductions.length} Deduction Fields Applied
            </p>
          </CardContent>
        </Card>

        {/* Net Salary Payable */}
        <Card className="border-l-4 border-l-emerald-500 shadow-sm bg-white hover:shadow transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Net Take-Home</p>
                <h3 className="text-2xl font-bold text-emerald-600 mt-1">
                  ₹{netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </h3>
              </div>
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                <CheckCircle2 className="w-6 h-6" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Direct Deposit to Bank Account
            </p>
          </CardContent>
        </Card>

        {/* Pay Period Status */}
        <Card className="border-l-4 border-l-purple-500 shadow-sm bg-white hover:shadow transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Status</p>
                <h3 className="text-xl font-bold text-gray-900 mt-1">
                  Disbursed
                </h3>
              </div>
              <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
                <Sparkles className="w-6 h-6" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {MONTH_NAMES[selectedMonth - 1]} {selectedYear} Cycle
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Salary Slip Document (Printable & Exportable) */}
      <div id="printable-salary-slip" className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        {/* Slip Header Banner */}
        <div className="bg-gradient-to-r from-blue-900 via-slate-800 to-blue-950 text-white p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center text-white font-bold text-2xl shadow-md">
                A
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight">{selectedEmployeeInfo.company}</h2>
                <p className="text-blue-200 text-xs mt-0.5">
                  Official Salary Slip & Earnings Statement
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                CONFIDENTIAL PAYSLIP
              </span>
              <p className="text-gray-300 text-sm font-medium mt-1">
                {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
              </p>
            </div>
          </div>
        </div>

        {/* Employee & Payroll Metadata Grid */}
        <div className="p-6 sm:p-8 border-b border-gray-200 bg-gray-50/50">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-sm">
            <div>
              <span className="text-xs font-semibold uppercase text-gray-400 block tracking-wider">
                Employee Name
              </span>
              <span className="font-semibold text-gray-900 text-base mt-0.5 block">
                {selectedEmployeeInfo.name}
              </span>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase text-gray-400 block tracking-wider">
                Employee ID
              </span>
              <span className="font-medium text-gray-800 mt-0.5 block font-mono">
                EMP-{selectedEmployeeInfo.id}
              </span>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase text-gray-400 block tracking-wider">
                Designation
              </span>
              <span className="font-medium text-gray-800 mt-0.5 block">
                {selectedEmployeeInfo.designation}
              </span>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase text-gray-400 block tracking-wider">
                Department
              </span>
              <span className="font-medium text-gray-800 mt-0.5 block">
                {selectedEmployeeInfo.department}
              </span>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase text-gray-400 block tracking-wider">
                PAN Number
              </span>
              <span className="font-medium text-gray-800 mt-0.5 block font-mono">
                {selectedEmployeeInfo.pan}
              </span>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase text-gray-400 block tracking-wider">
                Bank Account
              </span>
              <span className="font-medium text-gray-800 mt-0.5 block font-mono">
                {selectedEmployeeInfo.bankAccount}
              </span>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase text-gray-400 block tracking-wider">
                IFSC Code
              </span>
              <span className="font-medium text-gray-800 mt-0.5 block font-mono">
                {selectedEmployeeInfo.ifsc}
              </span>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase text-gray-400 block tracking-wider">
                Pay Period
              </span>
              <span className="font-medium text-gray-800 mt-0.5 block">
                01/{String(selectedMonth).padStart(2, '0')}/{selectedYear} - 30/{String(selectedMonth).padStart(2, '0')}/{selectedYear}
              </span>
            </div>
          </div>
        </div>

        {/* Breakdown of Components: Earnings vs Deductions */}
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
          {/* Earnings Column */}
          <div className="p-6">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <h3 className="font-bold text-gray-800 text-base uppercase tracking-wider flex items-center">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-600 mr-2"></span>
                Earnings
              </h3>
              <span className="text-xs font-semibold text-gray-400 uppercase">Amount (INR)</span>
            </div>

            <div className="divide-y divide-gray-100 text-sm mt-1">
              {earnings.length > 0 ? (
                earnings.map((item, idx) => (
                  <div key={item.salaryFieldId || idx} className="py-3 flex justify-between items-center">
                    <div>
                      <span className="text-gray-800 font-medium">{item.name}</span>
                      {item.isNullable && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200">
                          Standard
                        </span>
                      )}
                    </div>
                    <span className="font-semibold text-gray-900 font-mono">
                      ₹{item.displayValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                ))
              ) : (
                <div className="py-4 text-center text-gray-400 text-sm">
                  No earnings components available
                </div>
              )}
            </div>

            <div className="mt-4 pt-3 border-t-2 border-gray-200 flex justify-between items-center text-sm font-bold text-gray-900 bg-gray-50 p-3 rounded-lg">
              <span>Total Gross Earnings</span>
              <span className="text-base text-blue-600 font-mono">
                ₹{grossAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          {/* Deductions Column */}
          <div className="p-6 bg-gray-50/20">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <h3 className="font-bold text-gray-800 text-base uppercase tracking-wider flex items-center">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500 mr-2"></span>
                Deductions
              </h3>
              <span className="text-xs font-semibold text-gray-400 uppercase">Amount (INR)</span>
            </div>

            <div className="divide-y divide-gray-100 text-sm mt-1">
              {deductions.length > 0 ? (
                deductions.map((item, idx) => (
                  <div key={item.salaryFieldId || idx} className="py-3 flex justify-between items-center">
                    <div>
                      <span className="text-gray-800 font-medium">{item.name}</span>
                      {item.isNullable && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200">
                          Standard
                        </span>
                      )}
                    </div>
                    <span className="font-semibold text-rose-600 font-mono">
                      -₹{item.displayValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                ))
              ) : (
                <div className="py-4 text-center text-gray-400 text-sm">
                  No deduction components recorded
                </div>
              )}
            </div>

            <div className="mt-4 pt-3 border-t-2 border-gray-200 flex justify-between items-center text-sm font-bold text-gray-900 bg-gray-50 p-3 rounded-lg">
              <span>Total Deductions</span>
              <span className="text-base text-rose-600 font-mono">
                ₹{deductionsAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* Net Salary Summary Box */}
        <div className="p-6 sm:p-8 bg-gradient-to-r from-blue-50/70 via-indigo-50/40 to-slate-50 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-blue-900">
                Net Salary Payable
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                In words: <span className="font-semibold text-gray-700 italic">{numberToWords(netAmount)}</span>
              </p>
            </div>
            <div className="text-left sm:text-right">
              <span className="text-3xl font-extrabold text-emerald-600 tracking-tight font-mono">
                ₹{netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200/60 flex flex-col sm:flex-row sm:items-center justify-between text-xs text-gray-400 gap-2">
            <span>
              Generated via ApurvaX Payroll on {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            <span className="italic">
              This is a computer-generated salary slip and requires no physical signature.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Finance;
